import styled from '@emotion/styled'
import {
  ExtranetUpdateCustomerGroupDto,
  GetExtranetCustomerGroupQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledMultiSelect,
  ControlledSelect,
  ControlledSwitch,
  ExtranetContainer,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacColumn,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacH6,
  PharmacIcon,
  PharmacImage,
  PharmacPortalTooltip,
  PharmacSuccessToast,
  PharmacTab,
  PharmacTabs,
  PharmacText,
  PharmacTooltip,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Control, Controller, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ConfirmationModal } from '../../components/confirmation-modal/ConfirmationModal'
import { PricingContractInput } from '../../components/customer-groups/PricingContractInput'
import { ScheduleSlotSelector } from '../../components/customer-groups/ScheduleSlotInput'
import { mapCustomerGroupSearchParamsToQueryParams } from '../../components/customer-groups/util/query-parameter-mapping'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import LargePrintDoseBag from '../customer-group-settings/dose-bag-images/Iso_printti.png'
import NormalPrintDoseBag from '../customer-group-settings/dose-bag-images/Normaali_printti.png'
import { __customerCustomerGroupsQueryKeyFactory } from '../customer-groups/CustomerGroupsView'

const InfoFormContainer = styled(PharmacContainer)`
  background-color: var(--color-accent-2);
  border-radius: 0 20px 20px 20px;
`

export const CustomerGroupSettingsView = () => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { minDesktop, maxMobile } = useBreakpoint()
  const [updatedSettingsModal, setUpdatedSettingsModal] = useState<boolean>(false)
  const [warningModal, setWarningModal] = useState<boolean>(false)
  const { customer } = useCustomerContext()
  const { isPharmacCustomerService } = useAuthenticationContext()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()

  const [searchParams] = useSearchParams()

  const [params] = useState<GetExtranetCustomerGroupQueryDto>(
    mapCustomerGroupSearchParamsToQueryParams(searchParams)
  )

  const { data: customerGroupsData, isLoading: customerGroupsIsLoading } = useQuery({
    queryKey: __customerCustomerGroupsQueryKeyFactory(customer?.id),
    queryFn: async () =>
      await api.customers.getCustomerGroups(customer!.id!, {
        ...params,
      }),
  })

  const { data: availablePricingContracts } = useQuery({
    queryFn: async () =>
      await api.customers.getCustomerGroupPricing(customer?.id as number, id as string),
    queryKey: ['__customerGroupPricing', customer?.id, id],
    enabled: typeof customer?.id === 'number' && typeof id === 'string',
  })

  const { data: availableSchedules } = useQuery({
    queryFn: async () => await api.customers.getCustomerGroupScheduleSlots(customer?.id as number),
    queryKey: ['__customerGroupSchedule', customer?.id],
    enabled: typeof customer?.id === 'number',
  })

  const { data: availablePouchLayouts } = useQuery({
    queryFn: async () => await api.customers.getCustomerGroupPouchLayout(customer?.id as number),
    queryKey: ['__customerGroupPouch', customer?.id],
    enabled: typeof customer?.id === 'number',
  })

  const { data: customerGroupConsents } = useQuery({
    queryFn: async () => await api.customers.getCustomerGroupConsents(),
    queryKey: ['__customerGroupConcent'],
  })

  const customerGroup = customerGroupsData?.find((g) => g.groupId === id)

  const { mutate: updateCustomerGroup } = useMutation({
    mutationFn: async (dto: ExtranetUpdateCustomerGroupDto) => {
      return await api.customers.updateCustomerGroup(customer!.id!, id!, dto)
    },
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:customerGroupSettings.groupUpdatedSuccessfully')}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: __customerCustomerGroupsQueryKeyFactory(),
      })

      navigate(-1)
    },
  })

  const handleUpdateCustomerGroup = (dto: ExtranetUpdateCustomerGroupDto) => {
    updateCustomerGroup(dto)
    setUpdatedSettingsModal(false)
  }

  const formhook = useForm<ExtranetUpdateCustomerGroupDto>({
    mode: 'onChange',
    defaultValues: {
      dataReleaseConsentIds: [],
      omitStickers: false,
      pouchLayoutId: -1000,
      pricingProductId: 0,
      scheduleSlotId: -1000,
      pricingContractId: 0,
    },
  })

  const handlePouchLayoutIdChange = (value: string) => {
    if (value === '') return null

    return Number(value)
  }

  useEffect(() => {
    if (customerGroupsData && !customerGroupsIsLoading) {
      const customerGroup = customerGroupsData.find((a) => a.groupId === id)

      if (customerGroup) {
        formhook.reset({
          omitStickers: customerGroup.omitStickers || false,
          pricingProductId: customerGroup.pricingProductId,
          pouchLayoutId: customerGroup.layoutId,
          scheduleSlotId: customerGroup.scheduleSlot?.id,
          dataReleaseConsentIds: (Array.isArray(customerGroup.dataReleaseConsents)
            ? customerGroup.dataReleaseConsents
            : []
          ).map((a) => a.id),
          pricingContractId: customerGroup.pricingContractId,
        })
      }
    }
  }, [customerGroupsData, customerGroupsIsLoading])

  const scheduleSlotId = formhook.watch('scheduleSlotId')
  const omitStickers = formhook.watch('omitStickers')
  const pricingContractId = formhook.watch('pricingContractId')
  const pricingProductId = formhook.watch('pricingProductId')
  const pouchLayoutId = formhook.watch('pouchLayoutId')
  const dataReleaseConsentIds = formhook.watch('dataReleaseConsentIds')

  const checkIfValid = () => {
    const slotIsValid = typeof scheduleSlotId === 'number' && scheduleSlotId >= 0
    const omitStickersIsValid = typeof omitStickers === 'boolean'
    const pricingProductIdIsValid = typeof pricingProductId === 'number'
    const pricingContractIdIsValid = typeof pricingContractId === 'number'
    const pouchLayoutIsValid =
      pouchLayoutId === null || (typeof pouchLayoutId === 'number' && pouchLayoutId >= 0)
    const consentsIsValid =
      dataReleaseConsentIds === null ||
      (typeof dataReleaseConsentIds === 'object' && dataReleaseConsentIds.length >= 0)

    return (
      slotIsValid &&
      omitStickersIsValid &&
      pricingProductIdIsValid &&
      pricingContractIdIsValid &&
      pouchLayoutIsValid &&
      consentsIsValid
    )
  }

  console.log(
    'CustomerGroupSettingsView :: DEBUG :: render :: checkIfValid(): ',
    checkIfValid(),
    formhook.getValues()
  )

  return (
    <ExtranetWrapper>
      <PharmacColumn
        paddingLargeTop
        paddingLarge={!minDesktop}
        paddingMedium={maxMobile}
      >
        <PharmacTabs singleTab>
          {[
            <PharmacTab
              key={1}
              handle={t('extranet:customerGroupSettings.update')}
            >
              <InfoFormContainer
                paddingMedium
                paddingSmallHorizontal={maxMobile}
              >
                <PharmacFieldset style={maxMobile ? { padding: 0 } : {}}>
                  <PharmacFieldsetLegend marginSmallLeft={maxMobile}>
                    <PharmacH5>{customerGroup?.groupName}</PharmacH5>
                  </PharmacFieldsetLegend>
                  <FormProvider {...formhook}>
                    <ExtranetContainer
                      flexColumn
                      full
                      paddingMediumVertical
                    >
                      <ExtranetContainer
                        flexColumn
                        full
                        paddingMediumLeft
                      >
                        <ExtranetContainer flexRow>
                          <PharmacH6>
                            {t('extranet:customerGroupSettings.doseDispensingFee')}*
                          </PharmacH6>
                          <PharmacTooltip
                            maxWidth={maxMobile ? '300px' : '500px'}
                            paddingSmallLeft
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>
                              {t('extranet:customerGroupSettings.doseDispensingFeeTooltip')}
                            </PharmacText>
                          </PharmacTooltip>
                        </ExtranetContainer>
                        <PricingContractInput
                          formHook={formhook}
                          availablePricingContracts={availablePricingContracts || []}
                        />

                        <ExtranetContainer
                          flexRow
                          paddingMediumTop
                          paddingSmallBottom
                        >
                          <PharmacH6>{t('extranet:customerGroupSettings.orderTime')}*</PharmacH6>
                          <PharmacTooltip
                            paddingSmallLeft
                            maxWidth={maxMobile ? '300px' : '500px'}
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>
                              {t('extranet:customerGroupSettings.orderTimeTooltip')}
                            </PharmacText>
                          </PharmacTooltip>
                        </ExtranetContainer>
                        <ExtranetContainer
                          full
                          paddingMediumRight
                        >
                          <Controller
                            name="scheduleSlotId"
                            rules={{ required: true }}
                            control={formhook.control as unknown as Control<FieldValues>}
                            render={({ field }) => (
                              <ScheduleSlotSelector
                                nextOrderTime={
                                  customerGroup?.nextOrderTime
                                    ? formatDateTimeString(customerGroup?.nextOrderTime)
                                    : ''
                                }
                                error={
                                  !customerGroupsIsLoading && typeof field.value === 'undefined'
                                }
                                availableScheduleSlots={availableSchedules || []}
                                onChange={field.onChange}
                                selectedSlotId={field.value}
                              />
                            )}
                          />
                        </ExtranetContainer>
                        <ExtranetContainer
                          flexRow
                          paddingMediumTop
                        >
                          <PharmacH6>{t('extranet:customerGroupSettings.doseBagLayout')}</PharmacH6>
                          <PharmacPortalTooltip
                            paddingSmallLeft
                            placement="auto"
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <ExtranetContainer flexColumn>
                              <PharmacText>
                                {t('extranet:customerGroupSettings.doseBagLayoutTooltip')}
                              </PharmacText>
                              <ExtranetContainer
                                flexRow
                                paddingMediumTop
                              >
                                <ExtranetContainer
                                  flexColumn
                                  marginMediumRight
                                >
                                  <PharmacText>
                                    {t('extranet:customerGroupSettings.normalLayout')}
                                  </PharmacText>
                                  <PharmacImage
                                    marginMediumBottom
                                    marginLargeBottom
                                    width="100%"
                                    fit="contain"
                                    imageUrl={NormalPrintDoseBag}
                                  />
                                </ExtranetContainer>
                                <ExtranetContainer flexColumn>
                                  <PharmacText>
                                    {t('extranet:customerGroupSettings.bigFontLayout')}
                                  </PharmacText>
                                  <PharmacImage
                                    marginMediumBottom
                                    marginLargeBottom
                                    width="100%"
                                    fit="contain"
                                    imageUrl={LargePrintDoseBag}
                                  />
                                </ExtranetContainer>
                              </ExtranetContainer>
                            </ExtranetContainer>
                          </PharmacPortalTooltip>
                        </ExtranetContainer>
                        <PharmacContainer
                          marginSmallVertical
                          paddingMediumRight
                          style={{ width: '100%' }}
                        >
                          <ControlledSelect
                            control={formhook.control as unknown as Control<FieldValues>}
                            name="pouchLayoutId"
                            valueTransformer={handlePouchLayoutIdChange}
                          >
                            <option value="">
                              {t('extranet:customerGroupSettings.normalLayout')}
                            </option>
                            {availablePouchLayouts?.map((a) => (
                              <option
                                key={a.id}
                                value={a.id}
                              >
                                {a.name}
                              </option>
                            ))}
                          </ControlledSelect>
                        </PharmacContainer>
                        <ExtranetContainer
                          paddingMediumTop
                          flexRow
                        >
                          <PharmacH6>{t('extranet:customerGroupSettings.disclosure')}*</PharmacH6>
                          <PharmacTooltip
                            paddingSmallLeft
                            maxWidth={maxMobile ? '300px' : '500px'}
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>
                              {t('extranet:customerGroupSettings.disclosureTooltip')}
                            </PharmacText>
                          </PharmacTooltip>
                        </ExtranetContainer>
                        <PharmacContainer
                          marginSmallVertical
                          paddingMediumRight
                          style={{ width: '100%' }}
                        >
                          <ControlledMultiSelect
                            control={formhook.control as unknown as Control<FieldValues>}
                            name="dataReleaseConsentIds"
                          >
                            {customerGroupConsents?.map((a) => (
                              <option
                                key={a.key}
                                value={a.id}
                              >
                                {a.name}
                              </option>
                            ))}
                          </ControlledMultiSelect>
                        </PharmacContainer>
                        <ExtranetContainer
                          flexRow
                          paddingMediumTop
                          alignCenter
                        >
                          <PharmacH6 paddingSmallRight>
                            {t('extranet:customerGroupSettings.stickerlessGroup')}
                          </PharmacH6>
                          <PharmacTooltip
                            marginLargeRight={!maxMobile}
                            marginSmallRight={maxMobile}
                            maxWidth={maxMobile ? '300px' : '500px'}
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>
                              {t('extranet:customerGroupSettings.stickerlessGroupTooltip')}
                            </PharmacText>
                          </PharmacTooltip>
                          <ControlledSwitch
                            name="omitStickers"
                            control={formhook.control as unknown as Control<FieldValues>}
                          />
                        </ExtranetContainer>
                      </ExtranetContainer>
                    </ExtranetContainer>
                  </FormProvider>
                </PharmacFieldset>
                <ExtranetContainer
                  paddingLargeTop
                  full
                  justifyEnd={minDesktop}
                  justifyCenter={!minDesktop}
                >
                  <PharmacButton
                    fullWidth={!minDesktop}
                    variant="secondary"
                    marginLargeRight
                    onClick={() => navigate(-1)}
                  >
                    {t('common:cancel')}
                  </PharmacButton>
                  <PharmacButton
                    fullWidth={!minDesktop}
                    onClick={() => {
                      !!formhook.getValues().dataReleaseConsentIds.find((a) => a !== 0) &&
                      !formhook.getValues().omitStickers
                        ? setWarningModal(true)
                        : setUpdatedSettingsModal(true)
                    }}
                    disabled={
                      !formhook.formState.isDirty || !checkIfValid() || isPharmacCustomerService
                    }
                  >
                    {t('common:save')}
                  </PharmacButton>
                </ExtranetContainer>
                <ConfirmationModal
                  onClose={() => setWarningModal(false)}
                  showOnProp={warningModal}
                  closeButtonText={t('common:return')}
                  title={t('common:nb')}
                  content={
                    <PharmacText>
                      {t('extranet:customerGroupSettings.smilaGroupSticker')}
                    </PharmacText>
                  }
                />
                <ConfirmationModal
                  onClose={() => setUpdatedSettingsModal(false)}
                  onSubmit={formhook.handleSubmit(handleUpdateCustomerGroup, (jerrors) =>
                    console.log('onInvalid jerrorrs', jerrors)
                  )}
                  showOnProp={updatedSettingsModal}
                  closeButtonText={t('common:cancel')}
                  submitButtonText={t('common:save')}
                  title={
                    <PharmacH6>{t('extranet:customerGroupSettings.updateModal.title')}</PharmacH6>
                  }
                  content={
                    <ExtranetContainer
                      full
                      flexColumn
                    >
                      <PharmacText>
                        {t('extranet:customerGroupSettings.updateModal.content')}
                      </PharmacText>
                      <PharmacText paddingLargeTop>
                        {t('extranet:customerGroupSettings.updateModal.confirmation')}
                      </PharmacText>
                    </ExtranetContainer>
                  }
                />
              </InfoFormContainer>
            </PharmacTab>,
          ]}
        </PharmacTabs>
      </PharmacColumn>
    </ExtranetWrapper>
  )
}
